import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from '@/components/ui/sonner';
import { AuthProvider } from './contexts/AuthContext';
import LandingPage from './pages/LandingPage';
import PHLandingPage from './pages/PHLandingPage';
import ApplicationWizard from './pages/ApplicationWizard';
import FitnessWizard from './pages/FitnessWizard';
import AdminLogin from './pages/admin/AdminLogin';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminApplications from './pages/admin/AdminApplications';
import AdminApplicationDetail from './pages/admin/AdminApplicationDetail';
import AdminAffiliates from './pages/admin/AdminAffiliates';
import AdminLandingPages from './pages/admin/AdminLandingPages';
import AdminUsers from './pages/admin/AdminUsers';
import AdminActivity from './pages/admin/AdminActivity';
import AdminRiskAssessments from './pages/admin/AdminRiskAssessments';
import AcceptInvite from './pages/admin/AcceptInvite';
import QuoteView from './pages/QuoteView';
import RiskAssessment from './pages/RiskAssessment';
import UnifiedWizard from './pages/UnifiedWizard';
import LeagueAppsLandingPage from './pages/LeagueAppsLandingPage';
import HighstreetLandingPage from './pages/HighstreetLandingPage';
import PartnerPortal from './pages/PartnerPortal';
import ProtectedRoute from './components/ProtectedRoute';
import '@/App.css';

function App() {
  return (
    <HelmetProvider>
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Admin routes - must come before wildcard routes */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/accept-invite/:token" element={<AcceptInvite />} />
          <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
          <Route path="/admin/applications" element={<ProtectedRoute><AdminApplications /></ProtectedRoute>} />
          <Route path="/admin/applications/:id" element={<ProtectedRoute><AdminApplicationDetail /></ProtectedRoute>} />
          <Route path="/admin/affiliates" element={<ProtectedRoute><AdminAffiliates /></ProtectedRoute>} />
          <Route path="/admin/landing-pages" element={<ProtectedRoute><AdminLandingPages /></ProtectedRoute>} />
          <Route path="/admin/users" element={<ProtectedRoute><AdminUsers /></ProtectedRoute>} />
          <Route path="/admin/risk-assessments" element={<ProtectedRoute><AdminRiskAssessments /></ProtectedRoute>} />
          <Route path="/admin/activity" element={<ProtectedRoute><AdminActivity /></ProtectedRoute>} />
          
          {/* Public routes */}
          <Route path="/quote/:token" element={<QuoteView />} />
          {/* Legacy /apply/demo (no UTM capture) → canonical /quote/apply, preserving query params */}
          <Route path="/apply/demo" element={<ApplyDemoRedirect />} />
          <Route path="/apply/:affiliateSlug" element={<ApplicationWizard />} />
          <Route path="/apply-fitness/:affiliateSlug" element={<FitnessWizard />} />
          <Route path="/apply-fitness/:affiliateSlug/success" element={<FitnessSuccessPage />} />
          
          {/* Public tools */}
          <Route path="/risk-assessment" element={<RiskAssessment />} />
          <Route path="/risk-analyzer" element={<Navigate to="/risk-assessment" replace />} />
          
          {/* PH Landing Pages (for paid media / website integration) */}
          <Route path="/lp/:pageSlug" element={<PHLandingPage />} />

          {/* Bespoke affiliate landing page — LeagueApps */}
          <Route path="/leagueapps" element={<LeagueAppsLandingPage />} />

          {/* Bespoke affiliate landing page — Highstreet */}
          <Route path="/highstreet" element={<HighstreetLandingPage />} />

          {/* Affiliate partner portal (password-protected) */}
          <Route path="/partner/:slug" element={<PartnerPortal />} />
          
          {/* Direct quote routes (from PH landing pages) */}
          <Route path="/quote/apply" element={<UnifiedWizard />} />
          <Route path="/quote/sports" element={<ApplicationWizard directAccess={true} />} />
          <Route path="/quote/fitness" element={<FitnessWizard directAccess={true} />} />
          
          {/* Default and affiliate routes - must be last */}
          <Route path="/" element={<Navigate to="/demo" replace />} />
          <Route path="/:affiliateSlug" element={<LandingPage />} />
        </Routes>
        <Toaster position="top-right" />
      </BrowserRouter>
    </AuthProvider>
    </HelmetProvider>
  );
}

// Redirect stale /apply/demo links to the canonical quote entry, preserving UTM/query params
const ApplyDemoRedirect = () => {
  const location = useLocation();
  return <Navigate to={`/quote/apply${location.search}`} replace />;
};

// Simple success page component
const FitnessSuccessPage = () => (
  <div className="min-h-screen bg-[#F7F8FA] flex items-center justify-center px-6">
    <div className="max-w-md w-full text-center">
      <div className="bg-white rounded-2xl shadow-lg p-8 border border-[#E5E7EB] relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-[#6B5CE7] to-[#4ECDC4]"></div>
        <div className="w-16 h-16 bg-[#ECFDF5] rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="h-8 w-8 text-[#065F46]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h1 className="text-2xl font-black text-[#1A1A1A] mb-2 uppercase" style={{fontFamily: "'Barlow Condensed', sans-serif"}}>Application Submitted!</h1>
        <p className="text-[#6B7280] mb-6">
          Thank you for submitting your fitness studio insurance application. Our team will review your information and contact you with a quote within 1 business day.
        </p>
        <a 
          href="/"
          className="inline-block px-6 py-3 text-white font-bold rounded-lg uppercase tracking-wider text-sm hover:opacity-90 transition-all"
          style={{background: 'linear-gradient(135deg, #6B5CE7, #4ECDC4)', fontFamily: "'Barlow Condensed', sans-serif"}}
        >
          Return Home
        </a>
      </div>
    </div>
  </div>
);

export default App;
